// Captura de firma del paciente en el consentimiento informado — puerto a
// hce-chart de packages/hce/src/hce/consentimiento/CapturaFirmaPaciente.tsx
// (Módulo H, Grupo 2). Dos vías, mismo destino (un dataUrl que
// MotorFormularioConsentimiento.tsx sube como Binary+DocumentReference):
//   1. Tableta de firma integrada (SignaturePad, canvas + Pointer Events).
//   2. Escaneo/carga del formulario 024 oficial ya firmado en papel.
// Ninguna de las dos es la firma electrónica avanzada del profesional (esa
// va por Security Data, vía PanelFirmaImpresion/vaultClient) — es evidencia
// biométrica simple de consentimiento.
import { FileUp, Pencil } from 'lucide-react';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';

export type MetodoFirmaPaciente = 'tableta' | 'escaneo';

export interface FirmaPacienteCapturada {
  dataUrl: string;
  contentType: string;
  metodo: MetodoFirmaPaciente;
}

export interface CapturaFirmaPacienteProps {
  onCapturada: (firma: FirmaPacienteCapturada) => void;
  /**
   * Solo la vía de negativa (Bloque D del 024 oficial) lo pasa: si el
   * paciente rechaza el procedimiento Y se niega a firmar el propio
   * documento de negativa, este escape salta a la firma de un testigo en
   * su lugar (ver CapturaFirmaTestigo.tsx). La vía de aceptación nunca pasa
   * esta prop — el formulario oficial no contempla ese escape ahí.
   */
  onNegadoSinFirma?: () => void;
}

function leerArchivoComoDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

export function CapturaFirmaPaciente({ onCapturada, onNegadoSinFirma }: CapturaFirmaPacienteProps): JSX.Element {
  const { t } = useTranslation();
  const sigRef = useRef<SignaturePadHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sigVacia, setSigVacia] = useState(true);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  function confirmarTableta(): void {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError(t('consentimiento.firmaVacia'));
      return;
    }
    setError(null);
    onCapturada({ dataUrl: sigRef.current.toDataUrl(), contentType: 'image/png', metodo: 'tableta' });
  }

  async function confirmarEscaneo(): Promise<void> {
    if (!archivo) {
      setError(t('consentimiento.faltaArchivo'));
      return;
    }
    try {
      const dataUrl = await leerArchivoComoDataUrl(archivo);
      setError(null);
      onCapturada({ dataUrl, contentType: archivo.type || 'application/octet-stream', metodo: 'escaneo' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Tabs defaultValue="tableta">
        <TabsList>
          <TabsTrigger value="tableta">
            <Pencil className="size-4" /> {t('consentimiento.viaTableta')}
          </TabsTrigger>
          <TabsTrigger value="escaneo">
            <FileUp className="size-4" /> {t('consentimiento.viaEscaneo')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tableta" className="flex flex-col gap-2 pt-3">
          <SignaturePad ref={sigRef} onChange={setSigVacia} />
          <div className="flex justify-end">
            <Button type="button" onClick={confirmarTableta} disabled={sigVacia}>
              {t('consentimiento.confirmarFirma')}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="escaneo" className="flex flex-col gap-2 pt-3">
          <p className="text-sm text-hce-muted">{t('consentimiento.instruccionEscaneo')}</p>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              aria-label={t('consentimiento.elegirArchivo')}
              className="hidden"
              onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
            />
            <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
              {t('consentimiento.elegirArchivo')}
            </Button>
            {archivo && <span className="text-sm text-hce-ink">{archivo.name}</span>}
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={() => void confirmarEscaneo()} disabled={!archivo}>
              {t('consentimiento.confirmarFirma')}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
      {onNegadoSinFirma && (
        <div className="flex justify-end border-t border-hce-line pt-3">
          <Button type="button" variant="ghost" onClick={onNegadoSinFirma}>
            {t('consentimiento.negadoSinFirma')}
          </Button>
        </div>
      )}
    </div>
  );
}
